import React, { useEffect, useState } from "react";
import { Button, Nav, NavDropdown } from "react-bootstrap";
import withStyles from 'isomorphic-style-loader/withStyles';
import cx from 'classnames';
import { FormattedMessage } from "react-intl";
import { useDispatch, useSelector } from "react-redux";

import NavLink from "../../NavLink/NavLink";
import { openHeaderModal } from "../../../actions/siteadmin/modalActions";
import messages from "../../../locale/messages";
import history from "../../../history";
import getStatus from './getUserStatus.graphql'
import useQueryResponse from "../../../lib/hooks/useQueryResponse"
import lpApi from "../../../helpers/lpApi";
import s from './NavigationAfterLogin.css';

// Lightweight, read-only count of pending Large-Project quotes across the
// client's projects — surfaced as the nav badge so "quotes waiting" is visible
// at a glance. Reuses the new myProjectRequests.receivedQuoteCount field (one
// grouped COUNT on the api), summed client-side. Fired once, browser-only.
const Q_PENDING_QUOTES = `
  query {
    myProjectRequests {
      status
      projectRequests { id receivedQuoteCount }
    }
  }
`;

const NavigationAfterLogin = ({ afterDropdown, orderId, handleMenuClose }) => {

    const [location, setLocation] = useState('')
    const [pendingQuotes, setPendingQuotes] = useState(0)
    const dispatch = useDispatch()
    // LARGE PROJECTS FLAG: only show the "My Projects" (large-projects) link when
    // the LP module is ON. Mirrors Footer.js — read the SSR-seeded runtime store,
    // fail-closed (undefined -> hidden). Keeps the LP entry point cleanly separate
    // from the small-jobs booking nav.
    const largeProjectsEnabled = useSelector((state) => state?.runtime?.largeProjectsEnabled)

    useEffect(() => {
        setLocation(history?.location?.pathname)
    }, [history?.location?.pathname])

    // Sum receivedQuoteCount across the client's projects for the nav badge.
    // Best-effort: any failure (logged-out, no projects, api down) leaves the
    // badge hidden. Runs only in the browser, once on mount.
    useEffect(() => {
        if (typeof window === 'undefined') return undefined;
        let cancelled = false;
        (async () => {
            try {
                const res = await lpApi(Q_PENDING_QUOTES);
                const env = res && res.data && res.data.myProjectRequests;
                if (cancelled || !env || env.status !== 200) return;
                const total = (env.projectRequests || [])
                    .reduce((sum, p) => sum + (Number(p && p.receivedQuoteCount) || 0), 0);
                setPendingQuotes(total);
            } catch (e) {
                /* badge stays hidden on failure */
            }
        })();
        return () => { cancelled = true; };
    }, [])

    const { data, loading } = useQueryResponse({
        query: getStatus,
        ssr: false,
        fetchPolicy: 'network-only'
    })

    return (

        (afterDropdown ?
            <>
                <NavDropdown.Item as={NavLink} to="/dashboard" className={cx((location === '/dashboard/' || location === '/dashboard') && 'active')}>
                    <FormattedMessage {...messages.dashboard} />
                </NavDropdown.Item>
                <NavDropdown.Item as={NavLink} to="/bookings/upcoming" className={cx(location === '/bookings/upcoming' && 'active')}>
                    <FormattedMessage {...messages.myBookings} />
                </NavDropdown.Item>
                <NavDropdown.Item as={NavLink} to="/payment-method" className={cx(location === '/payment-method' && 'active')}>
                    <FormattedMessage {...messages.paymentMethodText} />
                </NavDropdown.Item>
                <NavDropdown.Item as={NavLink} to="/saved-addresses" className={cx(location === '/saved-addresses' && 'active')}>
                    <FormattedMessage {...messages.savedAddresses} />
                </NavDropdown.Item>
                <NavDropdown.Item as={NavLink} to="/profile" className={cx(location === '/profile' && 'active')}>
                    <FormattedMessage {...messages.profile} />
                </NavDropdown.Item>
                <NavDropdown.Item as={NavLink} to="/notifications" className={cx(location === '/notifications' && 'active')}>
                    <FormattedMessage {...messages.notifications} />
                </NavDropdown.Item>
                <NavDropdown.Item as={NavLink} to="/trusted-contacts" className={cx(location === '/trusted-contacts' && 'active')}>
                    <FormattedMessage {...messages.trustedContacts} />
                </NavDropdown.Item>
                {largeProjectsEnabled && (
                <NavDropdown.Item as={NavLink} to="/my-projects" className={cx(location === '/my-projects' && 'active')}>
                    <FormattedMessage {...messages.myProjects} />
                    {pendingQuotes > 0 && <span className={s.navQuoteBadge}>{pendingQuotes}</span>}
                </NavDropdown.Item>
                )}
                <NavDropdown.Item as={NavLink} to="/settings" className={cx(location === '/settings' && 'active')}>
                    <FormattedMessage {...messages.settings} />
                </NavDropdown.Item>
                <NavDropdown.Item as={NavLink} to="/support" className={cx(location === '/support' && 'active')}>
                    <FormattedMessage {...messages.support} />
                </NavDropdown.Item>
                <NavDropdown.Item as={NavLink} to="/delete-account" className={cx(location === '/delete-account' && 'active')}>
                    <FormattedMessage {...messages.deleteAccount} />
                </NavDropdown.Item>
                {orderId && <NavDropdown.Item as={NavLink} to={`/order/${orderId}`} className={cx(location === `/order/${orderId}` && 'active')}>
                    <FormattedMessage {...messages.myCart} />
                </NavDropdown.Item>}
                <NavDropdown.Item to="#">
                    <div onClick={() => dispatch(openHeaderModal('userLogout'))}><FormattedMessage {...messages.logout} /></div>
                </NavDropdown.Item>

            </>
            :
            <Nav className={s.headerNavSubListContainer} onSelect={handleMenuClose}>
                <NavLink to="/dashboard" className={cx(s.headerMenuItem, ((location === '/dashboard/' || location === '/dashboard') && s.activeTabMenu))} >
                    <FormattedMessage {...messages.dashboard} />
                </NavLink>
                <NavLink to="/bookings/upcoming" className={cx(s.headerMenuItem, (location === '/bookings/upcoming' && s.activeTabMenu))} >
                    <FormattedMessage {...messages.myBookings} />
                </NavLink>
                <NavLink to="/payment-method" className={cx(s.headerMenuItem, (location === '/payment-method' && s.activeTabMenu))}>
                    <FormattedMessage {...messages.paymentMethodText} />
                </NavLink>
                <NavLink to="/saved-addresses" className={cx(s.headerMenuItem, (location === '/saved-addresses' && s.activeTabMenu))}>
                    <FormattedMessage {...messages.savedAddresses} />
                </NavLink>
                <NavLink to="/profile" className={cx(s.headerMenuItem, (location === '/profile' && s.activeTabMenu))}>
                    <FormattedMessage {...messages.profile} />
                </NavLink>
                <NavLink to="/notifications" className={cx(s.headerMenuItem, (location === '/notifications' && s.activeTabMenu))}>
                    <FormattedMessage {...messages.notifications} />
                </NavLink>
                <NavLink to="/trusted-contacts" className={cx(s.headerMenuItem, (location === '/trusted-contacts' && s.activeTabMenu))}>
                    <FormattedMessage {...messages.trustedContacts} />
                </NavLink>
                {largeProjectsEnabled && (
                <NavLink to="/my-projects" className={cx(s.headerMenuItem, (location === '/my-projects' && s.activeTabMenu))}>
                    <FormattedMessage {...messages.myProjects} />
                    {pendingQuotes > 0 && <span className={s.navQuoteBadge}>{pendingQuotes}</span>}
                </NavLink>
                )}
                <NavLink to="/settings" className={cx(s.headerMenuItem, (location === '/settings' && s.activeTabMenu))}>
                    <FormattedMessage {...messages.settings} />
                </NavLink>
                <NavLink to="/support" className={cx(s.headerMenuItem, (location === '/support' && s.activeTabMenu))}>
                    <FormattedMessage {...messages.support} />
                </NavLink>
                <NavLink to="/delete-account" className={cx(s.headerMenuItem, (location === '/delete-account' && s.activeTabMenu))}>
                    <FormattedMessage {...messages.deleteAccount} />
                </NavLink>
                {orderId && <NavLink to={`/order/${orderId}`} className={cx(s.headerMenuItem, (location === `/order/${orderId}` && s.activeTabMenu))}>
                    <FormattedMessage {...messages.myCart} />
                </NavLink>}
                <NavLink to={''} className={cx(s.headerMenuItem)} onClick={() => dispatch(openHeaderModal('userLogout'))}>
                    <div><FormattedMessage {...messages.logout} /></div>
                </NavLink>
            </Nav>
        )

    )
}
export default withStyles(s)(NavigationAfterLogin)